import { useState, useCallback, useEffect, useRef } from 'react'
import { BlogMetadataRecord } from 'api/clients/BlogMetadataRecordsClient'

export interface BlogMetadataFormState {
  title: string
  author: string
  summary: string
  slug: string
  launchDate: string
}

export interface BlogMetadataFormHandlers {
  handleTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleAuthorChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleSummaryChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleSlugChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleLaunchDateChange: (date: string) => void
}

export interface UseBlogMetadataFormStateOptions {
  initialData?: BlogMetadataRecord
  onFieldChange?: (field: keyof BlogMetadataFormState, value: string) => void
}

export interface UseBlogMetadataFormStateReturn {
  formState: BlogMetadataFormState
  handlers: BlogMetadataFormHandlers
}

export const useBlogMetadataFormState = ({
  initialData,
  onFieldChange,
}: UseBlogMetadataFormStateOptions = {}): UseBlogMetadataFormStateReturn => {
  const [title, setTitle] = useState<string>(initialData?.title || '')
  const [author, setAuthor] = useState<string>(initialData?.author || '')
  const [summary, setSummary] = useState<string>(initialData?.summary || '')
  const [slug, setSlug] = useState<string>(initialData?.url_slug || '')
  const [launchDate, setLaunchDate] = useState<string>(initialData?.launch_date || '')

  // Track if we've synced with initialData to avoid overwriting user changes
  const hasSyncedRef = useRef(!!initialData)

  // Sync form state when initialData becomes available (loading -> loaded)
  useEffect(() => {
    if (initialData && !hasSyncedRef.current) {
      setTitle(initialData.title || '')
      setAuthor(initialData.author || '')
      setSummary(initialData.summary || '')
      setSlug(initialData.url_slug || '')
      setLaunchDate(initialData.launch_date || '')
      hasSyncedRef.current = true
    }
  }, [initialData])

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTitle = e.target.value
      setTitle(newTitle)
      onFieldChange?.('title', newTitle)
    },
    [onFieldChange],
  )

  const handleAuthorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newAuthor = e.target.value
      setAuthor(newAuthor)
      onFieldChange?.('author', newAuthor)
    },
    [onFieldChange],
  )

  const handleSummaryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newSummary = e.target.value
      setSummary(newSummary)
      onFieldChange?.('summary', newSummary)
    },
    [onFieldChange],
  )

  const handleSlugChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newSlug = e.target.value
      setSlug(newSlug)
      onFieldChange?.('slug', newSlug)
    },
    [onFieldChange],
  )

  const handleLaunchDateChange = useCallback(
    (date: string) => {
      setLaunchDate(date)
      onFieldChange?.('launchDate', date)
    },
    [onFieldChange],
  )

  return {
    formState: {
      title,
      author,
      summary,
      slug,
      launchDate,
    },
    handlers: {
      handleTitleChange,
      handleAuthorChange,
      handleSummaryChange,
      handleSlugChange,
      handleLaunchDateChange,
    },
  }
}
